import { useEffect, useRef } from "react";
import { detectBrowserLocale, type Locale } from "@chatcode/i18n";
import { updateUserSettings } from "@/lib/api";

/** One comparison on authenticated boot, then only real browser-language changes. */
export function useBrowserLocaleHint(
  userId: string | null,
  storedHint: Locale | undefined,
  onSaved: (hint: Locale) => void,
) {
  const callbackRef = useRef(onSaved);
  callbackRef.current = onSaved;
  const hintRef = useRef(storedHint);
  hintRef.current = storedHint;
  useEffect(() => {
    if (!userId) return;
    let stopped = false;
    let running = false;
    let lastSaved = hintRef.current;
    let requested: Locale | null = null;
    const sync = async () => {
      requested = detectBrowserLocale(navigator.languages?.length ? navigator.languages : [navigator.language]);
      if (running) return;
      running = true;
      try {
        while (!stopped && requested && requested !== lastSaved) {
          const hint = requested;
          requested = null;
          const { preferences } = await updateUserSettings({ last_web_locale: hint });
          if (stopped) return;
          // An older control plane can ignore the additive hint. Never claim it
          // was persisted, and do not turn that into a continuous retry loop.
          if (preferences.last_web_locale !== hint) return;
          lastSaved = hint;
          callbackRef.current(hint);
        }
      } catch { /* Best effort: compare again on next boot/language change. */ }
      finally { running = false; }
    };
    const onChange = () => { void sync(); };
    onChange();
    window.addEventListener("languagechange", onChange);
    return () => { stopped = true; window.removeEventListener("languagechange", onChange); };
    // storedHint/callback updates must not restart the authenticated-boot check.
  }, [userId]);
}
